<template>
  <section
    class="users-white-bg-sec archived-user-list"
    style="background-color: white"
  >
    <DxDataGrid
      :data-source="dataStore"
      :rtl-enabled="rtlEnabled"
      :ref="dataGridRefName"
      :remote-operations="true"
      :showBorders="false"
      :columnAutoWidth="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :rowAlternationEnabled="false"
      :showColumnLines="true"
      :showRowLines="true"
      :no-data-text="$t('common.grid.noRecordsYet')"
      @content-ready="contentReady($event)"
      @selection-changed="onSelectionChanged"
      @editor-preparing="onEditorPreparing($event)"
      :class="{
        'no-content': dataStore.length <= 0,
        archivedGridHeightWithAlert: getActiveSessionsCount,
        archivedGridHeight: !getActiveSessionsCount,
      }"
    >
      <DxStateStoring
        :enabled="true"
        type="custom"
        :customSave="customSaveArchivedUser"
        :customLoad="customLoadArchivedUser"
      />
      <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
      <DxFilterRow :visible="true" />

      <DxHeaderFilter :visible="true" />

      <DxColumnChooser
        :enabled="true"
        mode="select"
        :title="$t('common.grid.columnChooser')"
      />

      <DxExport :enabled="true" :allow-export-selected-data="false" />

      <DxSelection mode="multiple" :select-all-mode="'page'" />

      <DxPaging :page-size="50" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[5, 10, 25, 50, 100]"
        :display-mode="'full'"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :infoText="$t('common.page')"
      />

      <DxToolbar>
        <DxItem location="before" template="columnButtonTemplate" />
        <DxItem location="before" template="exportButtonTemplate" />
        <DxItem location="before" template="clearAllTemplate" />
      </DxToolbar>

      <DxColumn
        data-field="Image"
        :caption="this.$t('common.image')"
        cell-template="image-cell"
        :allow-filtering="false"
        :allow-editing="false"
        :show-in-column-chooser="true"
        :min-width="89"
        alignment="center"
        css-class="image-index"
        :allow-exporting="true"
        :visible="true"
      />

      <DxColumn
        data-field="FirstName"
        :caption="this.$t('common.firstName')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :visible="true"
        :min-width="200"
      />

      <DxColumn
        data-field="LastName"
        :caption="this.$t('common.lastName')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :visible="true"
        :min-width="200"
      />

      <DxColumn
        data-field="UserType"
        :caption="this.$t('common.userType')"
        :allow-editing="false"
        :visible="true"
        :allow-filtering="false"
        :allow-header-filtering="true"
      >
        <DxHeaderFilter :data-source="userTypeHeaderFilter" />
      </DxColumn>

      <DxColumn
        data-field="EmailAddress"
        :caption="this.$t('common.emailAddress')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :visible="true"
      />

      <DxColumn
        data-field="PhoneNumber"
        :caption="this.$t('common.phoneNumber')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :visible="true"
      />

      <DxColumn
        data-field="RFID"
        :min-width="200"
        :caption="this.$t('common.RFID')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :visible="true"
        cell-template="rfid-template"
      />

      <template #rfid-template="{ data }">
        {{ data.data.RFID ? rfidFormatter(data.data.RFID) : "-" }}
      </template>

      <DxColumn
        data-field="ID"
        :caption="this.$t('common.id')"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :allow-editing="false"
        :visible="false"
      />

      <DxColumn
        data-field="Status"
        :caption="this.$t('common.status')"
        :show-in-column-chooser="false"
        :allow-header-filtering="false"
        :filter-operations="allowedOperations"
        :allow-editing="false"
        :visible="false"
      />

      <DxColumn
        data-field="VaccineDocument"
        :caption="this.$t('dashboard.grid.vaccineDoc')"
        :allow-filtering="false"
        :allow-header-filtering="true"
        :allow-editing="false"
        :allow-sorting="false"
        :visible="true"
        cell-template="vaccine-document-cell"
      >
        <DxHeaderFilter :data-source="this.vaccine_documentHeaderFilter" />
      </DxColumn>

      <DxColumn
        data-field="label"
        :caption="this.$t('common.label')"
        :allow-editing="false"
        :visible="true"
        :allow-filtering="true"
        :allow-header-filtering="false"
        :allow-sorting="false"
        :calculate-cell-value="getLabels"
      >
      </DxColumn>

      <DxColumn
        :caption="this.$t('common.grid.actions')"
        :min-width="350"
        :allow-filtering="false"
        :allow-exporting="false"
        :allow-editing="false"
        :visible="true"
        css-class="select-actions select-actionsDAsh"
        cell-template="actions-cell"
      />

      <template #image-cell="{ data }">
        <div v-viewer class="images dash-img clearfix" style="margin: auto">
          <ImageCell
            v-if="!data.data.Image"
            :image="data.data.Image"
            :name="data.data.FirstName ? data.data.FirstName.charAt(0) : ''"
          />

          <img
            class="user-img img-fluid rounded-circle"
            v-if="data.data.Image"
            style="cursor: pointer"
            :src="data.data.Image"
          />
        </div>
      </template>

      <template #status-cell="{ data }">
        <div class="my-2 d-flex align-items-center p-1">
          <StatusCell :status="data.data.Status" :data="data.data.Data" />
        </div>
      </template>

      <template #vaccine-document-cell="{ data }">
        <span
          class="text-capitalize"
          @click="
            data.data.VaccineDocument != 'Unchecked' && setUserData(data.data)
          "
          v-b-modal="
            data.data.VaccineDocument != 'Unchecked' && 'vaccineDetailsModal'
          "
          >{{ data.data.VaccineDocument }}</span
        >
      </template>

      <template #usersSelectionCounterTemplate>
        <p class="toolbar-users-counter">
          {{ usersSelectedCounter }}
          {{
            usersSelectedCounter == 1
              ? $t("users.form.userSelected")
              : $t("users.form.usersSelected")
          }}
        </p>
      </template>

      <template #inviteToDashboardTemplate>
        <button
          @click="massAction('invite-to-dashboard')"
          class="toolbar-action-buttons toolbar-main"
        >
          <span class="custom-text">{{
            $t("users.form.inviteToDashboard")
          }}</span>
        </button>
      </template>

      <template #revokeFromDashboardTemplate>
        <div class="d-flex justify-content-between">
          <button
            @click="massAction('revoke-from-dashboard')"
            class="toolbar-action-buttons toolbar-alert"
          >
            <span class="custom-text">{{
              $t("users.form.revokeFromDashboard")
            }}</span>
          </button>
          <!-- <div class="v-line"></div> -->
        </div>
      </template>
      <template #massActionsNoteTemplate>
        <p class="toolbar-mass-actions-note">
          {{ $t("common.notes.massActionsNote") }}
        </p>
      </template>

      <template #exportButtonTemplate>
        <DxDropDownButton
          class="export-dropdown"
          :items="buttonData"
          :text="$t('common.grid.export')"
          @item-click="serverSideExport"
        />
      </template>

      <template #columnButtonTemplate>
        <button class="custom-columns" @click="showColumnChooserPopup()">
          <span class="custom-text">{{ $t("common.grid.columns") }} </span>
          <span class="custom-icon">
            <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
          </span>
        </button>
      </template>

      <template #clearAllTemplate>
        <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
          <div class="toolbar-custom-line"></div>
          <Button
            :text="$t('common.grid.clearFilters')"
            @click="reset()"
            styling-mode="outlined"
            type="danger"
            class="clear-CustomColums"
          />
        </div>
      </template>

      <template #actions-cell="{ data }">
        <ActionCell
          :userData="data.data.Data"
          @modalConfirmationUpdate="refreshUsersGrid"
        />
      </template>
    </DxDataGrid>
  </section>
</template>

<script>
import DxDataGrid, {
  DxStateStoring,
  DxColumn,
  DxPaging,
  DxPager,
  DxScrolling,
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxSelection,
  DxExport,
  DxItem,
  DxToolbar,
} from "devextreme-vue/data-grid";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { mapGetters, mapActions } from "vuex";
import { default as Button } from "devextreme-vue/button";
import ImageCell from "@/components/sharedCells/ImageCell";
import ActionCell from "@/components/users/ArchivedUsersActionCell";
import CustomStore from "devextreme/data/custom_store";
import Api from "../../../services/Api";
import store from "@/state/store";
import { usersColumns } from "@/utils/dataSource";
import { rfidFormatterMixin } from "@/utils/mixins/rfidFormatterMixin";

const dataGridRefName = "dataGridArchivedUsersList";

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}
function isNotEmpty(value) {
  return value !== undefined && value !== null && value !== "";
}
export default {
  mixins: [rfidFormatterMixin],
  components: {
    DxDataGrid,
    DxStateStoring,
    DxColumn,
    DxPaging,
    DxPager,
    DxScrolling,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxSelection,
    DxExport,
    Button,
    ImageCell,
    ActionCell,
    DxDropDownButton,
    DxItem,
    DxToolbar,
  },
  props: ["allLabels"],
  data() {
    return {
      rtlEnabled: false,
      dataGridRefName,
      isFoundSelection: false,
      usersColumns: usersColumns,
      userTypeHeaderFilter: [],
      buttonData: ["Excel", "CSV", "PDF"],
      allowedOperations: ["contains"],
      selectedOperation: "contains",
      sortExportObject: {
        sortField: "FirstName",
        sortOrder: "asc",
      },
      filterExportObject: {},
      showClearAll: false,
      statusFilterValue: ["", "", [""]],
      statusHeaderFilter: [
        {
          text: "Active",
          value: ["Status", "contains", ["Active"]],
        },
        {
          text: "Denied",
          value: ["Status", "contains", ["Denied"]],
        },
      ],
      systemUserHeaderFilter: [
        {
          text: "No",
          value: ["SystemUser", "contains", ["no"]],
        },
        {
          text: "Pending",
          value: ["SystemUser", "contains", ["pending"]],
        },
      ],
      vaccine_documentHeaderFilter: [
        {
          text: "Vaccinated",
          value: ["vaccine_document", "contains", ["Vaccinated"]],
        },
        {
          text: "Unchecked",
          value: ["vaccine_document", "contains", ["Unchecked"]],
        },
        {
          text: "Negative PCR",
          value: ["vaccine_document", "contains", ["Negative PCR"]],
        },
        {
          text: "Positive PCR",
          value: ["vaccine_document", "contains", ["Positive PCR"]],
        },
        {
          text: "Expired",
          value: ["vaccine_document", "contains", ["Expired"]],
        },
        {
          text: "Disabled",
          value: ["vaccine_document", "contains", ["Disabled"]],
        },
      ],
      dataStore: new CustomStore({
        key: "UniqueId",
        load: (loadOptions) => {
          console.log("load fn from custom store fired");
          let params = "?";
          let filterQuery = "";
          console.log("filterQuery", filterQuery);
          let sortObject = { sortField: null, sortOrder: "" };
          [
            "skip",
            "take",
            "requireTotalCount",
            "requireGroupCount",
            "sort",
            "filter",
            "totalSummary",
            "group",
            "groupSummary",
          ].forEach((i) => {
            console.log("forEach", i, loadOptions[i]);
            console.log("isnotempty", isNotEmpty(loadOptions[i]));
            if (i in loadOptions && isNotEmpty(loadOptions[i])) {
              params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              if (i == "filter") {
                console.log("loadOptions[i]", loadOptions[i]);
                filterQuery += `${JSON.stringify(loadOptions[i])}`;
              }
              if (i == "sort") {
                sortObject.sortField = loadOptions[i][0].selector;
                sortObject.sortOrder = loadOptions[i][0].desc ? "desc" : "asc";
              }
            }
          });
          // this.resetUsersData();
          console.log("1 params", params);
          params = params.slice(0, -1);
          console.log("2 params", params);
          let filters;
          let finalFilterObject = {};
          this.multipleFilters = [];
          if (filterQuery) filters = JSON.parse(filterQuery);
          if (filters) console.log("filters", filters);
          if (filters && typeof filters[0] === "string") {
            if (filters[0] == "!") {
              console.log("not");
              if (typeof filters[1] === "object") {
                const excludeIds = [];
                const columnHeader =
                  typeof filters[1][0] === "object"
                    ? filters[1][0][0] + "HeaderFilter"
                    : filters[1][0] + "HeaderFilter";
                const columnHeaderFilter =
                  columnHeader.charAt(0).toLowerCase() + columnHeader.slice(1);
                console.log(columnHeaderFilter); // name of column filter
                if (typeof filters[1][0] === "object") {
                  console.log("obj");
                  filters[1].map((item) => {
                    if (typeof item === "object") {
                      console.log("item", item[2][0]);
                      excludeIds.push(item[2][0]);
                    }
                  });
                } else {
                  console.log("else");
                  // filters[1].map(item => {
                  // if (typeof item === "object") {
                  console.log("item", filters[1][2][0]);
                  excludeIds.push(filters[1][2][0]);
                  // }
                  // });
                }
                console.log(this[columnHeaderFilter]);
                const arr = this[columnHeaderFilter].filter(
                  (el) => !excludeIds.includes(el.value[2][0])
                );
                console.log("arr", arr, excludeIds);
                if (typeof filters[1][0] === "object") {
                  finalFilterObject[filters[1][0][0]] = arr.map((item) => {
                    return item.value[2][0];
                  });
                } else {
                  finalFilterObject[filters[1][0]] = arr.map((item) => {
                    return item.value[2][0];
                  });
                }
                console.log("finalFilterObject", finalFilterObject);
                this.multipleFilters.push(finalFilterObject);
              }
            } else {
              finalFilterObject[filters[0]] =
                filters[1] === "contains" || filters[1] === "="
                  ? filters[2]
                  : filters[2];
            }

            this.multipleFilters.push(finalFilterObject);
            // }
          } else {
            if (filters)
              for (const filter of filters) {
                if (filter[0] == "!") {
                  // console.log('multiple filters not');
                  if (typeof filter[1] === "object") {
                    const excludeIds = [];
                    const columnHeader =
                      typeof filter[1][0] === "object"
                        ? filter[1][0][0] + "HeaderFilter"
                        : filter[1][0] + "HeaderFilter";
                    const columnHeaderFilter =
                      columnHeader.charAt(0).toLowerCase() +
                      columnHeader.slice(1);
                    console.log(columnHeaderFilter); // name of column filter
                    if (typeof filter[1][0] === "object") {
                      // console.log('obj');
                      filter[1].map((item) => {
                        if (typeof item === "object") {
                          // console.log('item', item[2][0]);
                          excludeIds.push(item[2][0]);
                        }
                      });
                    } else {
                      excludeIds.push(filter[1][2][0]);
                    }
                    const arr = this[columnHeaderFilter].filter(
                      (el) => !excludeIds.includes(el.value[2][0])
                    );
                    if (typeof filter[1][0] === "object") {
                      finalFilterObject[filter[1][0][0]] = arr.map((item) => {
                        return item.value[2][0];
                      });
                    } else {
                      finalFilterObject[filter[1][0]] = arr.map((item) => {
                        return item.value[2][0];
                      });
                    }
                    console.log("finalFilterObject", finalFilterObject);
                    this.multipleFilters.push(finalFilterObject);
                  }
                }
                let orExist;
                let andExist;
                if (typeof filter === "object") {
                  console.log("obj", filter);
                  orExist = filter.find((item) => item === "or");
                  andExist = filter.find((item) => item === "and");
                  if (filter[1] == "or") {
                    // Header Filter - Gather In One Condition
                    if (
                      filter[0][1] === ">=" ||
                      filter[0][1] === "<=" ||
                      filter[0][1] === "<"
                    ) {
                      const finalFilter = {};
                      const condition = [];
                      for (const filterItem of filter) {
                        if (filterItem[2]) condition.push(filterItem[2][0]);
                      }
                      finalFilter[filter[0][0]] = {
                        start: condition[0].replace(/\//g, "-"),
                        end: condition[1].replace(/\//g, "-"),
                      };
                      this.multipleFilters.push(finalFilter);
                    } else {
                      // Gather In One Condition
                      for (const filter of filter) {
                        if (filter[1] === "contains" || filter[1] === "=") {
                          const finalFilter = {};
                          finalFilter[filter[0]] =
                            filter[1] === "contains" || filter[1] === "="
                              ? filter[2]
                              : filter[2];

                          // Header Filter - Gather In One Condition
                          const filterExist = this.multipleFilters.find(
                            (item) => item[filter[0]]
                          );
                          if (!filterExist)
                            this.multipleFilters.push(finalFilter);
                          else filterExist[filter[0]].push(filter[2][0]);
                        }
                      }
                    }
                  } // -------------- Header Filter --------------------
                }
                if (
                  filter.length > 0 &&
                  filter[0] &&
                  filter[0] !== "Actions" &&
                  !orExist &&
                  !andExist
                ) {
                  if (filter[1] === "contains" || filter[1] === "=") {
                    const finalFilter = {};
                    finalFilter[filter[0]] =
                      filter[1] === "contains" || filter[1] === "="
                        ? filter[2]
                        : filter[2];

                    // Header Filter - Gather In One Condition
                    const filterExist = this.multipleFilters.find(
                      (item) => item[filter[0]]
                    );
                    if (!filterExist) this.multipleFilters.push(finalFilter);
                    else filterExist[filter[0]].push(filter[2][0]);
                  }
                } else if (andExist) {
                  for (const andFilter of filter) {
                    if (
                      andFilter.length > 0 &&
                      andFilter[0] &&
                      andFilter[0] !== "Actions"
                    ) {
                      if (andFilter[1] === "contains" || andFilter[1] === "=") {
                        const finalFilter = {};
                        finalFilter[andFilter[0]] =
                          andFilter[1] === "contains" || andFilter[1] === "="
                            ? andFilter[2]
                            : andFilter[2];
                        this.multipleFilters.push(finalFilter);
                      }
                    }
                  }
                }
              }
          }

          finalFilterObject =
            this.multipleFilters && this.multipleFilters.length > 0
              ? Object.assign({}, ...this.multipleFilters)
              : finalFilterObject;

            // Extract entries and filter only those with keys starting with "Data.customFields."
            const customFieldsEntries = Object.entries(finalFilterObject).filter(([key]) =>
              key.startsWith('Data.customFields.')
            );

            // Map the filtered entries to the new structure
            const customFieldsArray = customFieldsEntries.map(([key, value]) => {
              const customFieldId = key.split('.')[2]; // Extract the customFieldId part
              return {
                customFieldId,
                value,
              };
            });

            // Return the new structure
            const customFieldsFilter = {
              customFields: customFieldsArray,
            };

          this.filterExportObject = finalFilterObject;
          this.sortExportObject = sortObject;

          let queryParams = {
            filterBy: finalFilterObject,
            customFieldsFilter,
            orderBy: sortObject ? sortObject : { sortField: null },
          };

          if (this.isExport) {
            queryParams.isExporting = true;
          }

          Object.assign(queryParams.filterBy, { Status: ["Archived"] });

          return Api()
            .get(
              `/users/${store.getters["authfack/currentEntity"].id}${params}`,
              {
                params: queryParams,
              }
            )
            .then((data) => {
              const dataArr = [];
              data.data.users.map((user) => {
                let entity = this.currentEntity;
                let imageProfile = user.current_entity.image;
                if (imageProfile !== null) {
                  imageProfile = imageProfile.replace("entity_id", entity.id);
                }

                dataArr.push({
                  Image: imageProfile,
                  FirstName:
                    user.first_name && user.first_name.length
                      ? user.first_name
                      : "-",
                  LastName:
                    user.last_name && user.last_name.length
                      ? user.last_name
                      : "-",
                  UserType:
                    user.current_entity && user.current_entity.user_type
                      ? user.current_entity.user_type.name
                      : "-",
                  ID: user.user_id && user.user_id.length ? user.user_id : "-",
                  EmailAddress: user.email && user.email.length ? user.email : "-",
                  SystemUser: user.system_user == 0 ? "No" : "Yes",
                  // Status: user.is_active == 1 ? "Active" : "Deactivated",
                  VaccineDocument:
                    user.id == entity.id
                      ? ""
                      : user.last_document_vaccine || user.last_document_pcr
                      ? this.vaccineDocumentText(
                          user.last_document_vaccine,
                          user.last_document_pcr
                        )
                      : "Unchecked",
                  RFID: user.rf_ids?.length ? user.rf_ids : null,
                  PhoneNumber: user.phone ? user.phone : "-",
                  Data: user,
                  UniqueId: user.id,
                  FullName:
                    user.fullName && user.fullName.length ? user.fullName : "-",
                  LastVaccine: user.last_document_vaccine
                    ? user.last_document_vaccine
                    : user.last_document_pcr,
                  is_default_user: !!user.is_default_user,
                  VehicleRegistration: user.vehicle_registration
                    ? user.vehicle_registration
                    : "-",
                  CompanyName: user.company_name ? user.company_name : "-",
                  current_entity: user.current_entity,
                  labels: user.labels,
                });
              });

              // this.isServerSideExport = data.data.totalCount >= process.env.VUE_APP_EXPORT_LIST_COUNT;
              this.finalFilterObject = finalFilterObject;
              this.sortObject = sortObject ? sortObject : { sortField: null };
              this.totalCount = data.data.totalCount;
              return {
                data: dataArr,
                totalCount: data.data.totalCount,
                summary: data.data.summary,
                groupCount: data.data.length,
              };
            })
            .catch((err) => {
              throw new Error(err);
            });
        },
        // eslint-disable-next-line no-unused-vars
      }),
    };
  },

  computed: {
    ...mapGetters("grid", ["getTableHeaders"]),
    ...mapGetters("authfack", ["currentEntity"]),
    ...mapGetters("dailyLogbook", ["getUserTypes"]),
    ...mapGetters("emergency", ["getActiveSessionsCount"]),
    ...mapGetters("i18N", ["getLanguage"]),
    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
  },
  methods: {
    ...mapActions("dailyLogbook", ["fetchUserTypes"]),
    ...mapActions("users", ["resetUsersData"]),
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "exportJobBE",
      "createTableHeaders",
      "resetTableHeaders",
      "resetFilterValues",
    ]),
    ...mapActions("modal", ["onResetModal", "setData"]),
    async customLoadArchivedUser() {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/archived-users-dxgrid-list",
          headers: value,
          filters: "[]",
        });

        let value = JSON.parse(JSON.stringify(this.getTableHeaders));
        if (value) {
          value.pageIndex = 0;
          value.pageSize = 50;
        }

        if (value && value.columns) {
          for (let i = 0; i < value.columns.length; i++) {
            if (value.columns[i].dataField == "label") {
              value.columns[i].visibleIndex = 11;
            }
          }
        }

        return value;
      }
    },
    customSaveArchivedUser(gridState) {
      if (this.userEmail != "lt_techteam@visipoint.com") {
        gridState.selectedRowKeys = [];
        let value = JSON.stringify(gridState);

        console.log(value);
        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/archived-users-dxgrid-list",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          console.log("else");
          this.updateTableHeaders({
            url: "/archived-users-dxgrid-list",
            headers: value,
            filters: "[]",
          });
        }
      }
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/archived-users-dxgrid-list",
        headers: usersColumns,
      });
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    onSelectionChanged() {},
    onEditorPreparing(e) {
      if (e.parentType === 'filterRow' && e.editorName === 'dxTextBox') {
        const originalOnValueChanged = e.editorOptions.onValueChanged;

        e.editorOptions.onValueChanged = debounce((args) => {
          const value = args.value;

          // Only apply filter if 3 or more characters
          if (value && value.length >= 3) {
            originalOnValueChanged && originalOnValueChanged(args);
          } else if (value === '' || value == null) {
            // Allow clearing the filter
            originalOnValueChanged && originalOnValueChanged(args);
          } else {
            // Prevent filtering if less than 3 characters
            e.component.columnOption(e.dataField, 'filterValue', null);
          }
        }, 400); // debounce delay (ms)
      }
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.getLanguage === "en" ? "left top" : "right top",
        at: this.getLanguage === "en" ? "left top" : "right top",
        offset: "0 50",
      });
    },
    getAccess(rowData) {
      if (
        +rowData.current_entity.is_revoked == 1 ||
        +rowData.current_entity.is_revoked == 2
      )
        return "No";
      if (+rowData.current_entity.is_revoked == 3) return "Pending";
      if (
        +rowData.current_entity.is_revoked == 0 &&
        rowData.current_entity.role_id
      )
        return rowData.current_entity.role.name;
      return "No";
    },

    getLabels(rowData) {
      const labelArr = [];
      rowData.labels.map((item) => {
        const labelExist = this.allLabels.find(
          (label) => label.id == item.mapping_value
        );
        if (labelExist) labelArr.push(labelExist.name);
      });
      return labelArr.length ? labelArr.join(" - ") : "-";
    },
    showColumnChooserPopup() {
      this.dataGrid.showColumnChooser();
    },
    refreshGrid() {
      this.dataGrid.refresh();
    },
    refreshUsersGrid(data) {
      this.dataGrid.refresh();
      if (data && data.role_id) {
        this.$socket.emit("pingUpdateUserRole", {
          entity: this.currentEntity.id,
          user_id: data.id,
          role_id: data.role_id.id,
        });
      }
      this.$emit("onUpdate");
    },
    serverSideExport(e) {
      const columnNames = this.dataGrid.getVisibleColumns();
      const columns = [];
      for (let i = 0; i < columnNames.length; i++) {
        if (columnNames[i].allowExporting) {
          columns.push(columnNames[i].dataField);
        }
      }
      this.exportJobBE({
        type: e.itemData,
        headers: columns.reduce((accumulator, value) => {
          return { ...accumulator, [value]: value };
        }, {}),
        gridName: "users",
        entity_id: this.currentEntity.id,
        active: 0,
        // filterBy: this.filterExportObject,
        // orderBy: this.sortExportObject,
      });
      this.dataGrid.endUpdate();
    },
    vaccineDocumentText(docmentVaccine, docmentPcr) {
      let docment = null;
      if (docmentVaccine) {
        docment = docmentVaccine;
      } else {
        docment = docmentPcr;
      }
      let vaccine = null;
      if (docment.status == "enabled") {
        if (docment.type == "covid_pcr") {
          vaccine =
            docment.metadata.certificateModel.tests &&
            docment.metadata.certificateModel.tests[0].testResult == "Detected"
              ? "Positive PCR"
              : "Negative PCR";
        }
        if (
          docment.type == "covid_vaccine" ||
          docment.type == "covid_recovery"
        ) {
          if (docment.is_expiry == 1) {
            vaccine = "Expired";
          } else {
            vaccine = "Vaccinated";
          }
        }
      } else {
        if (docment.status == "deleted") {
          vaccine = "Unchecked";
        } else {
          vaccine = docment.status;
        }
      }
      return vaccine;
    },
    setUserData(data) {
      this.setData({
        type: "user_document",
        data,
      });
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
    },
  },
  mounted() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.fetchUserTypes().then(data => {
      data.data.map((userType) => {
        this.userTypeHeaderFilter.push({
          text: userType.name,
          value: ["UserType", "contains", [userType.id]],
        });
      });
    });
    this.fetchTableHeaders();
    console.log(this.dataStore);
  },
  destroyed() {
    this.resetTableHeaders();
    this.resetFilterValues();
    this.onResetModal();
    this.resetUsersData();
  },
  created() {
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });
  },
};
</script>

<style lang="scss">
.archivedGridHeight .dx-datagrid {
  height: calc(100vh - 290px);
}
.archivedGridHeightWithAlert .dx-datagrid {
  height: calc(100vh - 336px);
}
div#revoke-from-dashboard___BV_modal_content_ {
  background: #ffffff;
  border-radius: 26px;
  width: 579px;
  margin-top: 150px;
  div#revoke-from-dashboard___BV_modal_body_ {
    .massaction-confirm-modal .confirm-modal-body {
      width: 469px !important;
      line-height: 188% !important;
    }
    .massaction-confirm-modal .confirm-modal-body:lang(ar) {
    width: fit-content !important;
    line-height: 188% !important;
  }
  }
}
div#activeUsersListGridContainer {
  ul.dropdown-menu.show {
    align-items: center !important;
    background: #ffffff !important;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !important;
    border-radius: 6px !important;
    text-align: center !important;
    max-width: 195px !important;
    max-height: 179px !important;
    margin-top: 6px !important;
    overflow-y: scroll;
  }
  button.dropdown-item {
    margin: auto !important;

    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 19px !important;
    text-align: center !important;
    color: #787878 !important;
    padding-inline-end: 22px !important;
  }
  .dropdown-menu.show::-webkit-scrollbar {
    width: 9px;
  }
  .dropdown-menu.show::-webkit-scrollbar-track {
    background: #ededed;
    height: 129px;
    border-radius: 8.5px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }
  .dropdown-menu.show::-webkit-scrollbar-thumb {
    background: #666666;
    border-radius: 8.5px;
    height: 82px;
  }

  .dx-datagrid-rowsview .dx-row {
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;

    color: #495057;
  }
  .dx-datagrid-headers .dx-datagrid-table .dx-row > td {
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 17px !important;
    color: #939393 !important;
  }
  .dx-toolbar .dx-toolbar-before {
    padding-inline-end: 15px;
    display: flex;
    align-items: center;
  }
  .d-flex.justify-content-between.dx-template-wrapper.dx-item-content.dx-toolbar-item-content {
    align-items: center;
  }
  .toolbar-primary {
    background-color: #2680eb;
  }
  .toolbar-success {
    background-color: #34c38f;
  }
  .toolbar-alert {
    background-color: #f46a6a;
  }
  .toolbar-main {
    background-color: #103f5e;
  }
  .v-line {
    width: 1px;
    height: 23px;
    /* margin: 0 16px; */
    margin: 0px 8px 0px 24px;
    opacity: 1 !important;
    background-color: #ebebeb;
  }
  .dx-datagrid-rowsview .dx-selection > td .dx-link {
    color: #a5a5a5;
  }
  .dx-datagrid-rowsview .dx-selection.dx-row:hover > td .dx-link {
    color: #a5a5a5;
  }
  .resend-compliance-btn {
    border: none;
    background-color: #ffffff;
    padding: 0;
    margin: 0.5px 0 0 9px;
  }
  .resend-compliance-btn:disabled {
    opacity: 0.5;
    color: #a5a5a5;
  }

  .dx-row.dx-selection:has(.dx-checkbox.dx-state-disabled) {
    background-color: #efefef !important;
  }
  .dx-link.dx-link-edit.dx-state-disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.38;
  }
}
h5.modal-title {
  width: 100%;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.add-user-icon {
  font-size: 1.5rem;
  color: #103f5e;
  transition: 0.3s;
  cursor: pointer;
  &:hover {
    color: #34c38f;
  }
}

.users-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  border-radius: 8px !important;
}

.toolbar-mass-actions-note {
  margin-inline-start: 28px;

  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 17px !important;
  color: rgba(147, 147, 147, 0.87);
}

button.btn.d-flex.align-items-center.justify-content-center.text-center.top-users-btn.btn-primary.btn-sm.rounded-pill {
  height: 27px;
  width: 127px;

  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}

.diabledBtnTab {
  background: #afafaf !important;
}
.user-img,
.badge-secondary {
  height: 27px !important;
  width: 27px !important;
}

.dx-datagrid-column-chooser .dx-overlay-content {
  width: 250px !important;
}

.dx-overlay-content.dx-popup-normal.dx-resizable.dx-dropdowneditor-overlay {
  width: 250px !important;
}
[aria-label="Column Document - Vaccine/PCR"] {
  .dx-sort.dx-sort-up,
  .dx-sort.dx-sort-down {
    display: none !important;
  }
}
</style>
